import _get from "lodash/get";
import _orderBy from "lodash/orderBy";
import _includes from "lodash/includes";
import DOMPurify from "dompurify";

/**
 * This file contains redux selectors for accessing newsletter related data
 */

/**
 * Return the available newsletters
 * @param {*} state
 * @returns {*} - Object keyed by grouping ID. Includes *most recent* date in an array, groupingId, name
 */
export const getAvailableNewsletters = (state) => {
    return _get(state, "newsletters.availableNewsletters", {});
};

/**
 * Return the newsletter object by grouping id
 * @param {*} state
 * @param groupingId
 * @returns {*}
 */
export const getNewsletterByGroupingId = (state, groupingId) => {
    const newletters = getAvailableNewsletters(state);
    return _get(newletters, groupingId, {});
};

/**
 * Return the newsletter name by grouping id
 * @param state
 * @param groupingId
 * @returns {*}
 */
export const getNewsletterNameByGroupingId = (state, groupingId) => {
    const newsletter = getNewsletterByGroupingId(state, groupingId);
    return _get(newsletter, "name", "");
};

/**
 * Return the newsletter archive dates by grouping id
 * state.newsletters.availableNewsletters.archiveDates returns *most recent* newsletter date; array of 0-1 elements
 * see state.newsletters.archives for a list of *all* archiveDates by newsletter
 * @param {*} state
 * @param groupingId
 * @returns {*}
 */
export const getNewsletterArchiveDatesByGroupingId = (state, groupingId) => {
    const newsletter = getNewsletterByGroupingId(state, groupingId);
    return _get(newsletter, "archiveDates", []);
};

/**
 * Return the available archive dates for the current newsletter
 * @param {*} state
 * @returns {*}
 */
export const getCurrentNewsletterAvailableArchiveDates = (state) => {
    return _get(state, "newsletters.archives.archiveDates", []);
};

/**
 * Return the current newsletter groupingId for the current newsletter (From archives - does not work with temporary)
 * @param {*} state
 * @returns {*}
 */
export const getCurrentNewsletterGroupingId = (state) => {
    return _get(state, "newsletters.archives.groupingId", null);
};

/**
 * Return the current newsletter name for the current newsletter (From archives - does not work with temporary)
 * @param {*} state
 * @returns {*}
 */
export const getCurrentNewsletterNameFromArchives = (state) => {
    return _get(state, "newsletters.archives.name", "Newsletter");
};

/**
 * Return the current newsletter object(works with temporary)
 * @param {*} state
 * @returns {*}
 */
export const getCurrentNewsletter = (state) => {
    return _get(state, "newsletters.currentData.data", {});
};

/**
 * Return the current newsletter name for the current newsletter
 * @param {*} state
 * @returns {*}
 */
export const getCurrentNewsletterName = (state) => {
    const data = getCurrentNewsletter(state);
    return _get(data, "title", "Newsletter");
};

/**
 * Return the client logo src and alt text
 * @param {*} state
 * @returns {*}
 */
export const getClientLogoData = (state) => {
    const data = getCurrentNewsletter(state);
    const logoSrc = _get(data, "clientLogo", "");
    const logoAlt = _get(data, "clientName", "");
    return { logoSrc, logoAlt };
};

/**
 * Return the current newsletter edition (date). does not currently work for temporary CL-04
 * @param {*} state
 * @returns {*}
 */
export const getCurrentNewsletterEdition = (state) => {
    return _get(state, "newsletters.currentData.edition", "");
};

/**
 * Return whether the current newsletter is temporary
 * @param {*} state
 * @returns {*}
 */
export const isCurrentNewsletterTemporary = (state) => {
    return _get(state, "newsletters.isTemporary", false);
};

/**
 * Return whether the current newsletter has been hidden from client
 * @param {*} state
 * @returns {boolean}
 */
export const getIsNewsletterHidden = (state) => {
    return _get(state, "newsletters.currentData.isHidden", false);
};

/**
 * Return whether we are showing metrics for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const getShowMetrics = (state) => {
    return _get(state, "newsletters.currentData.showMetrics", false);
};

/**
 * Return whether we are showing full text for the current newsletter
 * @param {*} state
 * @returns {boolean}
 */
export const getShowFullText = (state) => {
    return _get(state, "newsletters.currentData.showFullText", false);
};

/**
 * Return the current newsletter legal statement for the current newsletter
 * @param {*} state
 * @returns {*}
 */
export const getCurrentNewsletterLegalStatement = (state) => {
    const data = getCurrentNewsletter(state);
    return DOMPurify.sanitize(_get(data, "legalStatement", ""));
};

/**
 * Return the header custom images legal statement for the current newsletter
 * @param {*} state
 * @returns {string}
 */
export const getCurrentNewsletterCustomImages = (state) => {
    const data = getCurrentNewsletter(state);
    return _get(data, "pubImages", []);
};


/**
 * Return Audio Reader URL for the current Newsletter
 * @param state
 * @returns {string}
 */
export const getAudioReaderBaseUrl = (state) => {
    const data = getCurrentNewsletter(state);
    return _get(data, "audioReaderUrl", "");
};
